<template>
  <div class="perfil-page">
    <div class="perfil-layout d-flex align-items-start">
      <aside v-if="!isMobile" class="perfil-sidebar pl-20">
        <TabsComponent
          class="perfil-tabs"
          :tabs="PERFIL_TABS"
          :initial-tab="activeTab"
          :mode="TABS_MODE.SCROLL"
          :layout="TABS_LAYOUT.VERTICAL"
          @tab-changed="handleTabChange" />
      </aside>

      <div class="perfil-content">
        <header class="perfil-header mb-35 mb-m-30">
          <div class="d-flex align-items-center mb-10">
            <CustomIcon :icon="leftIcon" size="16px" class="perfil-header-icon flex-shrink-0 me-2" />
            <span class="h6 semibold mb-0 perfil-header-title">{{ $t('subtitulo.miPerfil') }}</span>
          </div>
          <span class="p perfil-header-subtitle">{{ $t('subtitulo.gestionaCuenta') }}</span>
        </header>

        <section id="section-datos" class="perfil-section mb-35 mb-m-30">
          <span class="h6 semibold perfil-section-title d-block mb-10">{{ $t('subtitulo.datos') }}</span>
          <div class="perfil-card" :class="{ 'perfil-card-datos': !isEditingDatos, 'perfil-card-editing': isEditingDatos }">
            <div v-if="!isEditingDatos" class="perfil-card-actions perfil-card-actions-top">
              <button
                type="button"
                class="perfil-edit-btn p semibold d-flex align-items-center"
                @click="startEditDatos">
                {{ $t('editar') }}
                <CustomIcon :icon="editIcon" size="12px" class="ms-1" />
              </button>
            </div>

            <div v-if="!isEditingDatos" class="row g-20">
              <div class="col-12 col-md-6 d-flex flex-column gap-20">
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.name') }}
                  </div>
                  <div class="perfil-field-value p">{{ update.nombre || '—' }}</div>
                </div>
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.birthDate') }}
                  </div>
                  <div class="perfil-field-value p">{{ fechaNacimientoFormatted || '—' }}</div>
                </div>
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.email') }}
                  </div>
                  <div class="perfil-field-value perfil-field-value-break p">
                    {{ update.email || '—' }}
                  </div>
                </div>
              </div>
              <div class="col-12 col-md-6 d-flex flex-column gap-20">
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.lastName') }}
                  </div>
                  <div class="perfil-field-value p">{{ update.apellidos || '—' }}</div>
                </div>
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.phone') }}
                  </div>
                  <div class="perfil-field-value p">{{ update.telefono || '—' }}</div>
                </div>
              </div>
            </div>

            <div v-else class="row g-20">
              <div class="col-12 col-md-6 d-flex flex-column gap-20">
                <div class="perfil-field">
                  <label class="perfil-field-label small semibold" for="perfil-nombre">
                    {{ $t('home.form.personalInformation.name') }}
                  </label>
                  <input id="perfil-nombre" v-model="datosDraft.nombre" type="text" class="form-control perfil-input" />
                </div>
                <div class="perfil-field">
                  <label class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.birthDate') }}
                  </label>
                  <div class="perfil-datepicker">
                    <Datepicker
                      v-model="datosDraftFecha"
                      :locale="locale"
                      auto-apply
                      :format="formatDatePicker"
                      :enable-time-picker="false" />
                  </div>
                </div>
                <div class="perfil-field">
                  <label class="perfil-field-label small semibold" for="perfil-email">
                    {{ $t('home.form.personalInformation.email') }}
                  </label>
                  <input id="perfil-email" v-model="datosDraft.email" type="email" class="form-control perfil-input" />
                </div>
              </div>
              <div class="col-12 col-md-6 d-flex flex-column gap-20">
                <div class="perfil-field">
                  <label class="perfil-field-label small semibold" for="perfil-apellidos">
                    {{ $t('home.form.personalInformation.lastName') }}
                  </label>
                  <input
                    id="perfil-apellidos"
                    v-model="datosDraft.apellidos"
                    type="text"
                    class="form-control perfil-input" />
                </div>
                <div class="perfil-field">
                  <label class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.phone') }}
                  </label>
                  <div class="perfil-telefono">
                    <InputTelefono
                      v-model="datosDraft.telefono"
                      :placeholder="$t('home.form.personalInformation.phone')"
                      :tipo="TIPOS_TELEFONO.TIPO_B" />
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isEditingDatos" class="perfil-card-footer">
              <MyGocuButton :label="$t('botones.volver')" secondary @click="cancelEditDatos" />
              <MyGocuButton :label="$t('botones.guardarCambios')" @click="submitDatos" />
            </div>
          </div>
        </section>

        <section
          v-if="SHOW_DOCUMENTACION_PERSONAL"
          id="section-documentacion"
          class="perfil-section mb-35 mb-m-30">
          <span class="h6 semibold perfil-section-title d-block mb-10">
            {{ $t('subtitulo.documentacionPersonal') }}
          </span>
          <div class="perfil-card">
            <div class="row g-20">
              <div class="col-12 col-md-6">
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.ADni') }}
                  </div>
                  <label class="perfil-upload-pill p">
                    <span class="perfil-upload-pill-text">{{ anversoDniDisplayName }}</span>
                    <button
                      v-if="anversoDniFileName"
                      type="button"
                      class="perfil-upload-clear"
                      :aria-label="$t('general.borrar')"
                      @click.prevent="clearAnversoDni">
                      <i class="fa-solid fa-xmark" />
                    </button>
                    <UploadIcon v-else size="16px" color="var(--blue-1)" />
                    <input
                      v-if="!anversoDniFileName"
                      id="anversoDni"
                      type="file"
                      class="perfil-upload-input"
                      accept="image/gif, image/jpeg, image/png, application/pdf"
                      capture
                      @change="handleAnverso($event)" />
                  </label>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.RDni') }}
                  </div>
                  <label class="perfil-upload-pill p">
                    <span class="perfil-upload-pill-text">{{ reversoDniDisplayName }}</span>
                    <button
                      v-if="reversoDniFileName"
                      type="button"
                      class="perfil-upload-clear"
                      :aria-label="$t('general.borrar')"
                      @click.prevent="clearReversoDni">
                      <i class="fa-solid fa-xmark" />
                    </button>
                    <UploadIcon v-else size="16px" color="var(--blue-1)" />
                    <input
                      v-if="!reversoDniFileName"
                      id="reversoDni"
                      type="file"
                      class="perfil-upload-input"
                      accept="image/gif, image/jpeg, image/png, application/pdf"
                      capture
                      @change="handleReverso($event)" />
                  </label>
                </div>
              </div>
              <div class="col-12 col-md-6">
                <div class="perfil-field">
                  <div class="perfil-field-label small semibold">
                    {{ $t('home.form.personalInformation.anversoPasaporte') }}
                  </div>
                  <label class="perfil-upload-pill p">
                    <span class="perfil-upload-pill-text">{{ anversoPasaporteDisplayName }}</span>
                    <button
                      v-if="anversoPasaporteFileName"
                      type="button"
                      class="perfil-upload-clear"
                      :aria-label="$t('general.borrar')"
                      @click.prevent="clearAnversoPasaporte">
                      <i class="fa-solid fa-xmark" />
                    </button>
                    <UploadIcon v-else size="16px" color="var(--blue-1)" />
                    <input
                      v-if="!anversoPasaporteFileName"
                      id="anversoPasaporte"
                      type="file"
                      class="perfil-upload-input"
                      accept="image/gif, image/jpeg, image/png, application/pdf"
                      @change="handleAnversoPasaporte($event)" />
                  </label>
                </div>
              </div>
            </div>
            <div v-if="hasDocumentacionChanges" class="perfil-card-footer">
              <MyGocuButton :label="$t('botones.guardarCambios')" @click="updateDniData" />
            </div>
          </div>
        </section>

        <section id="section-password" class="perfil-section">
          <span class="h6 semibold perfil-section-title d-block mb-10">
            {{ $t('subtitulo.modificarcontrasenia') }}
          </span>
          <div class="perfil-card">
            <div class="d-flex flex-column perfil-password-fields">
              <div class="perfil-field">
                <label class="perfil-field-label small semibold" for="perfil-old-password">
                  {{ $t('home.form.personalInformation.password') }}
                </label>
                <div class="perfil-password-input">
                  <input
                    id="perfil-old-password"
                    v-model="actualizarPassword.oldPassword"
                    :type="showOldPassword ? 'text' : 'password'"
                    class="form-control perfil-input" />
                  <button
                    type="button"
                    class="perfil-password-toggle small"
                    @click="showOldPassword = !showOldPassword">
                    {{
                      showOldPassword
                        ? $t('home.form.personalInformation.ocultar')
                        : $t('home.form.personalInformation.mostrar')
                    }}
                  </button>
                </div>
              </div>
              <div class="perfil-field">
                <label class="perfil-field-label small semibold" for="perfil-new-password">
                  {{ $t('home.form.personalInformation.newpassword') }}
                </label>
                <div class="perfil-password-input">
                  <input
                    id="perfil-new-password"
                    v-model="actualizarPassword.password"
                    :type="showNewPassword ? 'text' : 'password'"
                    class="form-control perfil-input"
                    @focus="isNewPasswordFocused = true"
                    @blur="isNewPasswordFocused = false" />
                  <button
                    type="button"
                    class="perfil-password-toggle small"
                    @click="showNewPassword = !showNewPassword">
                    {{
                      showNewPassword
                        ? $t('home.form.personalInformation.ocultar')
                        : $t('home.form.personalInformation.mostrar')
                    }}
                  </button>
                </div>
                <div v-if="showPasswordValidation" class="perfil-password-validation">
                  <p
                    class="mb-0 perfil-validation-item small"
                    :class="pwRequirements.minLength ? 'perfil-validation-item-ok' : 'perfil-validation-item-error'">
                    <i class="fa-solid fa-xs me-2" :class="pwRequirements.minLength ? 'fa-check' : 'fa-xmark'" />
                    {{ $t('home.form.personalInformation.password8CharReq') }}
                  </p>
                  <p
                    class="mb-0 perfil-validation-item small"
                    :class="pwRequirements.hasNumber ? 'perfil-validation-item-ok' : 'perfil-validation-item-error'">
                    <i class="fa-solid fa-xs me-2" :class="pwRequirements.hasNumber ? 'fa-check' : 'fa-xmark'" />
                    {{ $t('home.form.personalInformation.password1NumReq') }}
                  </p>
                  <p
                    class="mb-0 perfil-validation-item small"
                    :class="
                      pwRequirements.hasUpperCase ? 'perfil-validation-item-ok' : 'perfil-validation-item-error'
                    ">
                    <i class="fa-solid fa-xs me-2" :class="pwRequirements.hasUpperCase ? 'fa-check' : 'fa-xmark'" />
                    {{ $t('home.form.personalInformation.password1MayusReq') }}
                  </p>
                </div>
              </div>
              <div class="perfil-field">
                <label class="perfil-field-label small semibold" for="perfil-confirm-password">
                  {{ $t('home.form.personalInformation.confirmnewpassword') }}
                </label>
                <div class="perfil-password-input">
                  <input
                    id="perfil-confirm-password"
                    v-model="actualizarPassword.passwordConfirm"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-control perfil-input" />
                  <button
                    type="button"
                    class="perfil-password-toggle small"
                    @click="showConfirmPassword = !showConfirmPassword">
                    {{
                      showConfirmPassword
                        ? $t('home.form.personalInformation.ocultar')
                        : $t('home.form.personalInformation.mostrar')
                    }}
                  </button>
                </div>
                <p
                  v-if="showPasswordConfirmValidation"
                  class="mb-0 perfil-validation-item small"
                  :class="passwordsMatch ? 'perfil-validation-item-ok' : 'perfil-validation-item-error'">
                  <i class="fa-solid fa-xs me-2" :class="passwordsMatch ? 'fa-check' : 'fa-xmark'" />
                  {{
                    passwordsMatch
                      ? $t('home.form.personalInformation.passwordsCoinciden')
                      : $t('home.form.personalInformation.passwordsNoCoinciden')
                  }}
                </p>
              </div>
            </div>
            <div v-if="hasPasswordChanges" class="perfil-card-footer">
              <MyGocuButton
                :label="$t('botones.actualizarContrasenia')"
                :disabled="!canSubmitPassword"
                @click="updatePassword" />
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, onBeforeUnmount, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import moment from 'moment-timezone';
  import Datepicker from '@vuepic/vue-datepicker';
  import { createToaster } from '@meforma/vue-toaster';
  import UsuarioApiService from '../../services/api/UsuarioApiService';
  import TabsComponent from '@/components/tabs/TabsComponent.vue';
  import CustomIcon from '@/components/CustomIcon.vue';
  import UploadIcon from '@/components/Icons/UploadIcon.vue';
  import InputTelefono from '@/components/inputs/InputTelefono.vue';
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import { TIPOS_TELEFONO } from '@/constants/inputsConstants';
  import { TABS_LAYOUT, TABS_MODE } from '@/helpers/tabsUtils';
  import { controlErrorActualizarPassword, controlErrorActualizarDni } from '@/services/utils/ErrorControl';
  import { useLoadingStore } from '@/store_pinia/loadingStore';
  import leftIcon from '@/assets/icons/raw/izquierda.svg';
  import editIcon from '@/assets/icons/edit.svg';

  const MOBILE_BREAKPOINT = 768;
  // Temporary: hide until backend returns documentation on GET /datos
  const SHOW_DOCUMENTACION_PERSONAL = false;

  const { t, locale } = useI18n();
  const loadingStore = useLoadingStore();
  const toaster = createToaster({ position: 'top-right' });

  const update = reactive({
    apellidos: null,
    codigoPostal: null,
    direccion: null,
    dni: null,
    email: null,
    emailConfirm: null,
    fechaNacimiento: null,
    mailingPublicidadActivo: null,
    nombre: null,
    paisId: null,
    poblacion: null,
    provincia: null,
    telefono: null,
    numSoporteDni: null,
    pasaporte: null,
    numSoportePasaporte: null,
  });

  const actualizarPassword = reactive({
    oldPassword: null,
    password: null,
    passwordConfirm: null,
  });

  const datosDraft = reactive({
    nombre: '',
    apellidos: '',
    email: '',
    telefono: '',
  });

  const isMobile = ref(window.innerWidth <= MOBILE_BREAKPOINT);
  const activeTab = ref('datos');
  const isEditingDatos = ref(false);
  const datosDraftFecha = ref(null);
  const showOldPassword = ref(false);
  const showNewPassword = ref(false);
  const showConfirmPassword = ref(false);
  const isNewPasswordFocused = ref(false);
  const hasDocumentacionChanges = ref(false);
  const anversoDni = ref();
  const reversoDni = ref();
  const anversoPasaporte = ref();
  const anversoDniFileName = ref();
  const reversoDniFileName = ref();
  const anversoPasaporteFileName = ref();

  const pwRequirements = reactive({
    minLength: false,
    hasUpperCase: false,
    hasNumber: false,
  });

  const PERFIL_TABS = [
    { id: 'datos', label: t('subtitulo.datos') },
    ...(SHOW_DOCUMENTACION_PERSONAL
      ? [{ id: 'documentacion', label: t('subtitulo.documentacionPersonal') }]
      : []),
    { id: 'password', label: t('subtitulo.modificarcontrasenia') },
  ];

  const fechaNacimientoFormatted = computed(() => {
    if (!update.fechaNacimiento) {
      return null;
    }
    return moment(update.fechaNacimiento).format('DD/MM/YYYY');
  });

  const anversoDniDisplayName = computed(
    () => anversoDniFileName.value ?? t('home.form.personalInformation.selectedFile')
  );
  const reversoDniDisplayName = computed(
    () => reversoDniFileName.value ?? t('home.form.personalInformation.selectedFile')
  );
  const anversoPasaporteDisplayName = computed(
    () => anversoPasaporteFileName.value ?? t('home.form.personalInformation.selectedFile')
  );

  const isPasswordStrong = computed(
    () => pwRequirements.minLength && pwRequirements.hasUpperCase && pwRequirements.hasNumber
  );

  const hasPasswordChanges = computed(
    () => !!actualizarPassword.oldPassword || !!actualizarPassword.password || !!actualizarPassword.passwordConfirm
  );

  const showPasswordValidation = computed(
    () => isNewPasswordFocused.value || (actualizarPassword.password?.length ?? 0) > 0
  );

  const showPasswordConfirmValidation = computed(() => (actualizarPassword.passwordConfirm?.length ?? 0) > 0);

  const passwordsMatch = computed(
    () =>
      !!actualizarPassword.password &&
      !!actualizarPassword.passwordConfirm &&
      actualizarPassword.password === actualizarPassword.passwordConfirm
  );

  const canSubmitPassword = computed(
    () => !!actualizarPassword.oldPassword && isPasswordStrong.value && passwordsMatch.value
  );

  function handleTabChange(tabId) {
    activeTab.value = tabId;
  }

  function formatDatePicker(fechaNacimiento) {
    if (!fechaNacimiento) {
      return '';
    }
    return moment(fechaNacimiento).format('DD/MM/YYYY');
  }

  function startEditDatos() {
    datosDraft.nombre = update.nombre ?? '';
    datosDraft.apellidos = update.apellidos ?? '';
    datosDraft.email = update.email ?? '';
    datosDraft.telefono = update.telefono ?? '';
    datosDraftFecha.value = update.fechaNacimiento ? moment(update.fechaNacimiento).toDate() : null;
    isEditingDatos.value = true;
  }

  function cancelEditDatos() {
    isEditingDatos.value = false;
  }

  async function submitDatos() {
    try {
      loadingStore.show();
      const fechaNacimiento = datosDraftFecha.value
        ? moment(datosDraftFecha.value).format('YYYY-MM-DD')
        : update.fechaNacimiento;
      const payload = {
        ...update,
        nombre: datosDraft.nombre,
        apellidos: datosDraft.apellidos,
        email: datosDraft.email,
        telefono: datosDraft.telefono,
        fechaNacimiento,
      };
      if (payload.dni) {
        payload.dni = payload.dni.toUpperCase();
      }
      if (payload.pasaporte) {
        payload.pasaporte = payload.pasaporte.toUpperCase();
      }
      if (payload.paisId) {
        payload.paisId = Number(payload.paisId);
      }
      await UsuarioApiService.modificarDatosUsuario(payload);
      update.nombre = payload.nombre;
      update.apellidos = payload.apellidos;
      update.email = payload.email;
      update.telefono = payload.telefono;
      update.fechaNacimiento = payload.fechaNacimiento;
      toaster.success(t('home.form.personalInformation.datosActualizados'));
      isEditingDatos.value = false;
    } catch (error) {
      console.error(error);
    } finally {
      loadingStore.hide();
    }
  }

  function checkDevice() {
    isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
  }

  async function updateDniData() {
    try {
      loadingStore.show();
      const formDNI = new FormData();
      if (anversoDni.value) formDNI.append('anversoDni', anversoDni.value);
      if (reversoDni.value) formDNI.append('reversoDni', reversoDni.value);
      if (anversoPasaporte.value) formDNI.append('anversoPasaporte', anversoPasaporte.value);
      await UsuarioApiService.uploadDniImages(formDNI);
      toaster.success(t('home.form.personalInformation.documentacionActualizada'));
      hasDocumentacionChanges.value = false;
    } catch (error) {
      console.error(error);
      controlErrorActualizarDni(error);
    } finally {
      loadingStore.hide();
    }
  }

  async function updatePassword() {
    if (!canSubmitPassword.value) {
      return;
    }
    try {
      loadingStore.show();
      await UsuarioApiService.modificarPassword(actualizarPassword);
      toaster.success(t('home.form.personalInformation.contraseniaActualizada'));
    } catch (error) {
      console.error(error);
      controlErrorActualizarPassword(error);
    } finally {
      actualizarPassword.oldPassword = null;
      actualizarPassword.password = null;
      actualizarPassword.passwordConfirm = null;
      showOldPassword.value = false;
      showNewPassword.value = false;
      showConfirmPassword.value = false;
      isNewPasswordFocused.value = false;
      pwRequirements.minLength = false;
      pwRequirements.hasUpperCase = false;
      pwRequirements.hasNumber = false;
      loadingStore.hide();
    }
  }

  function handleDocumentacionChanged() {
    hasDocumentacionChanges.value = true;
  }

  function handleAnverso($event) {
    const target = $event.target;
    anversoDni.value = target.files[0];
    anversoDniFileName.value = target.files[0].name;
    handleDocumentacionChanged();
  }

  function handleReverso($event) {
    const target = $event.target;
    reversoDni.value = target.files[0];
    reversoDniFileName.value = target.files[0].name;
    handleDocumentacionChanged();
  }

  function handleAnversoPasaporte($event) {
    const target = $event.target;
    anversoPasaporte.value = target.files[0];
    anversoPasaporteFileName.value = target.files[0].name;
    handleDocumentacionChanged();
  }

  function clearAnversoDni() {
    anversoDni.value = undefined;
    anversoDniFileName.value = undefined;
    handleDocumentacionChanged();
  }

  function clearReversoDni() {
    reversoDni.value = undefined;
    reversoDniFileName.value = undefined;
    handleDocumentacionChanged();
  }

  function clearAnversoPasaporte() {
    anversoPasaporte.value = undefined;
    anversoPasaporteFileName.value = undefined;
    handleDocumentacionChanged();
  }

  watch(
    () => actualizarPassword.password,
    newPassword => {
      const value = newPassword ?? '';
      pwRequirements.minLength = value.length >= 8;
      pwRequirements.hasUpperCase = /[A-Z]/.test(value);
      pwRequirements.hasNumber = /[0-9]/.test(value);
    }
  );

  onMounted(async () => {
    try {
      loadingStore.show();
      checkDevice();
      window.addEventListener('resize', checkDevice);

      const res = await UsuarioApiService.obtenerDatosUsuario();
      update.apellidos = res.apellidos;
      update.codigoPostal = res.codigoPostal;
      update.direccion = res.direccion;
      update.dni = res.dni;
      update.email = res.email;
      update.fechaNacimiento = res.fechaNacimiento;
      update.mailingPublicidadActivo = res.mailingPublicidadActivo;
      update.nombre = res.nombre;
      update.poblacion = res.poblacion;
      update.provincia = res.provincia;
      update.telefono = res.telefono;
      update.numSoporteDni = res.numSoporteDni;
      update.pasaporte = res.pasaporte;
      update.numSoportePasaporte = res.numSoportePasaporte;
    } catch (error) {
      console.error(error);
    } finally {
      loadingStore.hide();
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', checkDevice);
  });
</script>

<style lang="scss" scoped>
  .perfil-page {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 224px 32px 64px;
    box-sizing: border-box;
    color: var(--blue-1);
  }

  .perfil-layout {
    gap: 20px;
  }

  .perfil-sidebar {
    width: 290px;
    flex-shrink: 0;
    position: sticky;
    top: 120px;
    align-self: flex-start;
    border-left: 1px solid rgba(var(--blue-1-rgb), 0.2);
  }

  .perfil-content {
    flex: 1;
    max-width: 890px;
    min-width: 0;
  }

  .perfil-card {
    position: relative;
    background: white;
    border: 1px solid rgba(var(--blue-1-rgb), 0.2);
    border-radius: var(--bs-border-radius-lg);
    padding: 16px 8px;
  }

  .perfil-card.perfil-card-datos {
    padding-right: 88px;
  }

  .perfil-card-actions {
    z-index: 1;
  }

  .perfil-card-actions-top {
    position: absolute;
    top: 16px;
    right: 8px;
  }

  .perfil-card-footer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 20px;
  }

  .perfil-edit-btn {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
  }

  .perfil-edit-btn:hover {
    opacity: 0.8;
  }

  .perfil-field-label {
    margin-bottom: 2px;
  }

  .perfil-field-value-break {
    overflow-wrap: break-word;
  }

  .perfil-upload-pill {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 44px;
    padding: 10px 16px;
    margin-bottom: 0;
    background: white;
    border: 1px solid rgba(var(--blue-1-rgb), 0.2);
    border-radius: var(--bs-border-radius-pill);
    cursor: pointer;
  }

  .perfil-upload-pill-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .perfil-upload-clear {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: var(--blue-1);
    cursor: pointer;
  }

  .perfil-upload-clear:hover {
    opacity: 0.7;
  }

  .perfil-upload-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  .perfil-password-fields {
    gap: 15px;
  }

  .perfil-password-input {
    position: relative;
  }

  .perfil-input {
    background: white;
    border: 1px solid rgba(var(--blue-1-rgb), 0.2);
    border-radius: var(--bs-border-radius-lg);
    color: var(--blue-1);
    text-align: start;
  }

  .perfil-input:focus {
    border-color: rgba(var(--blue-1-rgb), 0.4);
    box-shadow: none;
  }

  .perfil-datepicker {
    :deep(.dp__input) {
      background: white;
      border: 1px solid rgba(var(--blue-1-rgb), 0.2);
      border-radius: var(--bs-border-radius-lg);
      color: var(--blue-1);
      text-align: start;
    }
  }

  .perfil-telefono {
    :deep(.input-telefono-tipo-b) {
      background: white !important;
      border: 1px solid rgba(var(--blue-1-rgb), 0.2) !important;
      border-radius: var(--bs-border-radius-lg) !important;
      color: var(--blue-1) !important;
      min-height: 38px;
      padding: 0 12px !important;

      .vti__input {
        color: var(--blue-1) !important;
      }
    }
  }

  .perfil-password-input .perfil-input {
    padding-right: 88px;
  }

  .perfil-password-toggle {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    background: none;
    border: none;
    padding: 0;
    text-decoration: underline;
    cursor: pointer;
  }

  .perfil-password-validation {
    margin-top: 8px;
  }

  .perfil-validation-item-ok {
    color: var(--verde-600);
  }

  .perfil-validation-item-error {
    color: var(--rojo-600);
  }

  :deep(.perfil-tabs.tabs-container.vertical) {
    position: static;
    background: transparent;

    .tabs-content {
      gap: 20px;
    }

    .tab-item {
      align-items: stretch;
      width: 270px;
    }

    .tab-button {
      width: 100%;
      min-height: 19px;
      padding: 0;
      text-align: left;
      color: var(--blue-1);
      font-size: 16px;
      font-weight: 400;
      line-height: 1;
      background: transparent;
    }

    .tab-button.active {
      font-weight: 600;
      background: transparent;
    }

    .tab-button:hover {
      opacity: 0.8;
    }

    .tab-indicator,
    .tab-indicator.active {
      display: none !important;
      width: 0 !important;
    }
  }

  @media screen and (max-width: 768px) {
    .perfil-page {
      max-width: 100%;
      padding: 120px 15px 32px;
    }

    .perfil-layout {
      display: block;
    }

    .perfil-content {
      max-width: 100%;
    }

    .perfil-card-actions-top {
      position: absolute;
      top: 16px;
      right: 8px;
    }
  }
</style>
